import {
  addMonths,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  startOfDay,
  startOfMonth,
  startOfYear,
  subMonths,
} from "date-fns";
import { ptBR } from "date-fns/locale";
import { createPortal } from "react-dom";
import {
  forwardRef,
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { subscribeToDismissFloatingLayers } from "../_shared/dismissFloatingLayers";
import { cn } from "../../utils/cn";
import { buildDataId, slugifyDataId } from "../../utils/dataId";
import { getFieldShellClassName, getFieldShellErrorStyle } from "../../utils/fieldShell";
import { DSIcon } from "../DSIcon";
import { capitalizeMonthLabel, resolveDatePickerPanelStyle } from "./datePickerUtils";

type DsDatePickerValue = Date | null;
type DsDatePickerViewMode = "month" | "year";

type DsDatePickerProps = {
  "data-id"?: string;
  className?: string;
  defaultOpen?: boolean;
  defaultValue?: DsDatePickerValue;
  description?: string;
  disabled?: boolean;
  errorMessage?: string;
  invalid?: boolean;
  label: string;
  maxDate?: Date;
  minDate?: Date;
  onOpenChange?: (open: boolean) => void;
  onValueChange?: (value: DsDatePickerValue) => void;
  open?: boolean;
  placeholder?: string;
  required?: boolean;
  value?: DsDatePickerValue;
} & Omit<
  ComponentPropsWithoutRef<"div">,
  "children" | "className" | "defaultValue" | "onChange" | "value"
>;

type OverlayStyle = {
  left: number;
  top: number;
  width: number;
};

const YEAR_RANGE_START = 1924;
const YEAR_RANGE_END = 2099;
const DAY_GRID_COLUMNS = 7;

function setButtonRefValue(
  ref: ForwardedRef<HTMLButtonElement> | undefined,
  value: HTMLButtonElement | null
) {
  if (!ref) {
    return;
  }

  if (typeof ref === "function") {
    ref(value);
    return;
  }

  ref.current = value;
}

function normalizeDateValue(value: DsDatePickerValue | undefined) {
  if (!value) {
    return null;
  }

  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function formatMonthTitle(date: Date) {
  return capitalizeMonthLabel(format(date, "LLL yyyy", { locale: ptBR }).replace(".", ""));
}

function formatDateLabel(date: Date) {
  return format(date, "dd/MM/yyyy");
}

function formatDayLabel(date: Date) {
  return format(date, "d 'de' MMMM 'de' yyyy", { locale: ptBR });
}

function formatDatePickerValueForBackend(value: DsDatePickerValue) {
  if (!value) {
    return null;
  }

  const iso = new Date(
    Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), 0, 0, 0, 0)
  ).toISOString();

  return iso.replace(".000Z", "Z");
}

function setDateYear(date: Date, year: number) {
  const nextDate = new Date(year, date.getMonth(), date.getDate());

  if (nextDate.getMonth() !== date.getMonth()) {
    return new Date(year, date.getMonth() + 1, 0);
  }

  return nextDate;
}

function isDateDisabled(date: Date, minDate?: Date, maxDate?: Date) {
  if (minDate && isBefore(startOfDay(date), startOfDay(minDate))) {
    return true;
  }

  if (maxDate && isAfter(endOfDay(date), endOfDay(maxDate))) {
    return true;
  }

  return false;
}

function isMonthDisabled(monthDate: Date, minDate?: Date, maxDate?: Date) {
  const previousMonth = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthDate);

  if (minDate && isBefore(monthEnd, startOfDay(minDate))) {
    return true;
  }

  if (maxDate && isAfter(previousMonth, endOfDay(maxDate))) {
    return true;
  }

  return false;
}

const DsDatePicker = forwardRef<HTMLButtonElement, DsDatePickerProps>(function DsDatePicker(
  {
    className,
    defaultOpen = false,
    defaultValue,
    description,
    disabled = false,
    errorMessage,
    invalid = false,
    label,
    maxDate,
    minDate,
    onOpenChange,
    onValueChange,
    open,
    placeholder,
    required = false,
    value,
    "data-id": dataIdProp,
    ...props
  },
  forwardedRef
) {
  const generatedId = useId();
  const panelId = `${generatedId}-panel`;
  const descriptionId = description ? `${generatedId}-description` : undefined;
  const hasErrorMessage = Boolean(errorMessage);
  const errorId = hasErrorMessage ? `${generatedId}-error` : undefined;
  const dataId = dataIdProp ?? buildDataId("date-picker", slugifyDataId(label));
  const rootProps = { ...props, "data-id": dataId };
  const buttonId = `${generatedId}-button`;
  const yearGridId = `${generatedId}-year-grid`;
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen);
  const [uncontrolledValue, setUncontrolledValue] = useState<DsDatePickerValue>(
    normalizeDateValue(defaultValue)
  );
  const [draftValue, setDraftValue] = useState<DsDatePickerValue>(
    normalizeDateValue(defaultValue)
  );
  const [viewMode, setViewMode] = useState<DsDatePickerViewMode>("month");
  const [viewMonth, setViewMonth] = useState<Date>(
    startOfMonth(normalizeDateValue(defaultValue) ?? new Date())
  );
  const [panelStyle, setPanelStyle] = useState<OverlayStyle | null>(null);
  const draftValueRef = useRef<DsDatePickerValue>(normalizeDateValue(defaultValue));
  const committedValueRef = useRef<DsDatePickerValue>(normalizeDateValue(defaultValue));

  const isControlled = value !== undefined;
  const committedValue = isControlled ? value : uncontrolledValue;
  const committedValueKey = committedValue ? committedValue.getTime() : null;
  const isOpen = open ?? uncontrolledOpen;
  draftValueRef.current = draftValue;
  committedValueRef.current = committedValue;
  const displayedText = committedValue ? formatDateLabel(committedValue) : placeholder ?? "DD/MM/AAAA";
  const years = useMemo(() => {
    return Array.from(
      { length: YEAR_RANGE_END - YEAR_RANGE_START + 1 },
      (_, index) => YEAR_RANGE_START + index
    );
  }, []);

  function commitOpen(nextOpen: boolean) {
    if (open === undefined) {
      setUncontrolledOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  }

  function commitValue(nextValue: DsDatePickerValue) {
    if (value === undefined) {
      setUncontrolledValue(nextValue);
    }

    onValueChange?.(nextValue);
  }

  function valuesAreEqual(left: DsDatePickerValue, right: DsDatePickerValue) {
    return (left?.getTime() ?? null) === (right?.getTime() ?? null);
  }

  function openPicker() {
    if (disabled) {
      return;
    }

    if (isOpen) {
      closePicker();
      return;
    }

    commitOpen(true);
  }

  function closePicker({ commitDraft = true }: { commitDraft?: boolean } = {}) {
    if (commitDraft) {
      const nextValue = draftValueRef.current;

      if (!valuesAreEqual(nextValue, committedValueRef.current)) {
        commitValue(nextValue);
      }
    }

    commitOpen(false);
  }

  function handleClear() {
    setDraftValue(null);
    draftValueRef.current = null;
    commitValue(null);
  }

  function handleDaySelect(date: Date) {
    if (isDateDisabled(date, minDate, maxDate)) {
      return;
    }

    const nextValue = normalizeDateValue(date);
    setDraftValue(nextValue);
    draftValueRef.current = nextValue;
    setViewMonth(startOfMonth(date));
    commitValue(nextValue);
    commitOpen(false);
  }

  function handleYearSelect(year: number) {
    const nextMonth = new Date(viewMonth);
    nextMonth.setFullYear(year);
    setViewMonth(startOfMonth(nextMonth));

    if (draftValue) {
      const nextDraft = setDateYear(draftValue, year);
      setDraftValue(nextDraft);
      draftValueRef.current = nextDraft;
    }

    setViewMode("month");
  }

  function handleMonthChange(nextMonth: Date) {
    setViewMonth(startOfMonth(nextMonth));
  }

  useEffect(() => {
    if (!isOpen) {
      setDraftValue(committedValue);
      setViewMode("month");
      return;
    }

    const nextMonth = committedValue ?? new Date();
    setDraftValue(committedValue);
    setViewMonth(startOfMonth(nextMonth));
    setViewMode("month");
  }, [committedValueKey, isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPanelStyle(null);
      return;
    }

    const updatePanelPosition = () => {
      setPanelStyle(resolveDatePickerPanelStyle(buttonRef.current));
    };

    updatePanelPosition();
    window.addEventListener("resize", updatePanelPosition);
    window.addEventListener("scroll", updatePanelPosition, true);

    return () => {
      window.removeEventListener("resize", updatePanelPosition);
      window.removeEventListener("scroll", updatePanelPosition, true);
    };
  }, [isOpen, viewMode]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        closePicker();
      }
    }

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    return subscribeToDismissFloatingLayers(() => {
      closePicker();
    });
  }, [isOpen]);

  useEffect(() => {
    setButtonRefValue(forwardedRef, buttonRef.current);
  }, [forwardedRef]);

  const monthDays = useMemo(() => {
    return eachDayOfInterval({
      end: endOfMonth(viewMonth),
      start: startOfMonth(viewMonth),
    });
  }, [viewMonth]);

  const monthWeeks = useMemo(() => {
    const firstDayOfMonth = startOfMonth(viewMonth);
    const offset = firstDayOfMonth.getDay();
    const totalCells = Math.ceil((offset + monthDays.length) / DAY_GRID_COLUMNS) * DAY_GRID_COLUMNS;
    const cells: Array<Date | null> = [];

    for (let index = 0; index < totalCells; index += 1) {
      const dayIndex = index - offset;
      cells.push(dayIndex >= 0 && dayIndex < monthDays.length ? monthDays[dayIndex] ?? null : null);
    }

    return cells;
  }, [monthDays, viewMonth]);

  const yearRangeDisabled = useMemo(() => {
    return years.map(year => {
      const yearStart = startOfYear(new Date(year, 0, 1));
      const yearEnd = endOfYear(yearStart);

      return {
        disabled: Boolean(
          (minDate && isBefore(yearEnd, startOfDay(minDate))) ||
            (maxDate && isAfter(yearStart, endOfDay(maxDate)))
        ),
        year,
      };
    });
  }, [maxDate, minDate, years]);

  const hasError = hasErrorMessage || invalid;
  const headerTitle = formatMonthTitle(viewMonth);

  return (
    <div className={cn("flex flex-col gap-3 font-inter", className)} data-slot="date-picker-root" {...rootProps}>
      <div
        className={cn(
          "bg-light-gray",
          hasError ? "shadow-none" : "shadow-[0_1px_2px_rgba(16,24,40,0.05)]",
          getFieldShellClassName({ hasError, isActive: isOpen })
        )}
        data-slot="date-picker-field"
        style={getFieldShellErrorStyle(hasError)}
      >
        <button
          aria-controls={panelId}
          aria-describedby={[descriptionId, errorId].filter(Boolean).join(" ") || undefined}
          aria-expanded={isOpen}
          aria-haspopup="dialog"
          aria-invalid={hasError || undefined}
          aria-label={label}
          className={cn(
            "flex min-h-14 w-full items-center gap-2 border-0 bg-transparent px-4 py-2 text-left shadow-none outline-none transition-colors duration-200 ease-out",
            disabled ? "cursor-not-allowed opacity-70" : "cursor-pointer"
          )}
          data-id={buildDataId(dataId, "trigger")}
          data-slot="date-picker-trigger"
          disabled={disabled}
          id={buttonId}
          onClick={openPicker}
          ref={node => {
            buttonRef.current = node;
            setButtonRefValue(forwardedRef, node);
          }}
          type="button"
        >
        <span className="flex size-5 shrink-0 items-center justify-center text-gray-5" data-slot="date-picker-icon">
          <DSIcon aria-hidden="true" name="Calendar" size={20} />
        </span>

        <span className="flex min-w-0 flex-1 flex-col justify-center gap-1" data-slot="date-picker-text">
          <span
            className="truncate text-[14px] font-semibold leading-[normal] tracking-[0px] text-gray-5"
            data-slot="date-picker-label"
          >
            {label}
            {!committedValue && required ? <span className="text-gray-5">*</span> : null}
          </span>

          <span
            className={cn(
              "truncate text-[14px] leading-[normal] tracking-[0px]",
              committedValue ? "font-normal text-dark-3" : "font-normal text-gray-5"
            )}
            data-slot="date-picker-value"
          >
            {displayedText}
          </span>
        </span>
      </button>
      </div>

      {description ? (
        <p
          className="text-left text-[12px] font-normal leading-[normal] tracking-[0px] text-gray-5"
          data-slot="date-picker-description"
          id={descriptionId}
        >
          {description}
        </p>
      ) : null}

      {errorMessage ? (
        <p
          className="text-left text-[12px] font-normal leading-[normal] tracking-[0px] text-feedback-danger"
          data-slot="date-picker-error"
          id={errorId}
        >
          {errorMessage}
        </p>
      ) : null}

      {isOpen && typeof document !== "undefined"
        ? createPortal(
            <div
              className="fixed inset-0 z-[60] bg-transparent"
              data-slot="date-picker-overlay"
              onClick={event => {
                if (event.target === event.currentTarget) {
                  closePicker();
                }
              }}
            >
              <div
                aria-label={label}
                aria-modal="false"
                className={cn(
                  "fixed overflow-hidden rounded-[12px] bg-light-gray shadow-[0_12px_40px_rgba(17,17,17,0.12)]",
                  "flex w-full flex-col"
                )}
                data-id={buildDataId(dataId, "panel")}
                data-slot="date-picker-panel"
                id={panelId}
                role="dialog"
                style={
                  panelStyle
                    ? {
                        left: `${panelStyle.left}px`,
                        top: `${panelStyle.top}px`,
                        width: `${panelStyle.width}px`,
                      }
                    : undefined
                }
              >
                <div className="flex h-10 items-center justify-between px-6" data-slot="date-picker-header">
                  <button
                    aria-expanded={viewMode === "year"}
                    aria-controls={yearGridId}
                    aria-label={viewMode === "year" ? "Fechar seleção de ano" : "Abrir seleção de ano"}
                    className="inline-flex cursor-pointer items-center gap-1 rounded-full px-2 py-1 text-[14px] font-normal leading-[normal] tracking-[0px] text-dark-3 transition-colors duration-150"
                    data-id={buildDataId(dataId, "view-mode-toggle")}
                    onClick={() => setViewMode(current => (current === "month" ? "year" : "month"))}
                    type="button"
                  >
                    <span>{headerTitle}</span>
                    <DSIcon
                      aria-hidden="true"
                      className={cn("size-5 text-dark-3 transition-transform duration-150", viewMode === "year" ? "rotate-180" : "")}
                      name="ArrowDown2"
                      size={16}
                    />
                  </button>

                  <div className="flex items-center gap-2" data-slot="date-picker-navigation">
                    <button
                      aria-label="Mês anterior"
                      className={cn(
                        "inline-flex size-8 cursor-pointer items-center justify-center rounded-full transition-colors duration-150",
                        isMonthDisabled(subMonths(viewMonth, 1), minDate, maxDate)
                          ? "cursor-not-allowed text-gray-4"
                          : "text-dark-3 hover:bg-brand-primary-3-20"
                      )}
                      data-id={buildDataId(dataId, "previous-month-button")}
                      disabled={isMonthDisabled(subMonths(viewMonth, 1), minDate, maxDate)}
                      onClick={() => handleMonthChange(subMonths(viewMonth, 1))}
                      type="button"
                    >
                      <DSIcon aria-hidden="true" name="ArrowLeft2" size={20} />
                    </button>

                    <button
                      aria-label="Próximo mês"
                      className={cn(
                        "inline-flex size-8 cursor-pointer items-center justify-center rounded-full transition-colors duration-150",
                        isMonthDisabled(addMonths(viewMonth, 1), minDate, maxDate)
                          ? "cursor-not-allowed text-gray-4"
                          : "text-dark-3 hover:bg-brand-primary-3-20"
                      )}
                      data-id={buildDataId(dataId, "next-month-button")}
                      disabled={isMonthDisabled(addMonths(viewMonth, 1), minDate, maxDate)}
                      onClick={() => handleMonthChange(addMonths(viewMonth, 1))}
                      type="button"
                    >
                      <DSIcon aria-hidden="true" name="ArrowRight2" size={20} />
                    </button>
                  </div>
                </div>

                {viewMode === "month" ? (
                  <div className="flex flex-col px-6 pb-2" data-slot="date-picker-month-view">
                    <div className="grid grid-cols-7 text-center text-[12px] font-normal leading-[normal] tracking-[0px] text-dark-3">
                      {["D", "S", "T", "Q", "Q", "S", "S"].map((weekday, index) => (
                        <span className="flex items-center justify-center whitespace-nowrap" key={`${weekday}-${index}`}>
                          {weekday}
                        </span>
                      ))}
                    </div>

                    <div className="grid grid-cols-7 gap-y-1 py-2" data-slot="date-picker-days">
                      {monthWeeks.map((date, index) => {
                        if (!date) {
                          return <span aria-hidden="true" className="size-9" key={`empty-${index}`} />;
                        }

                        const isSelected = Boolean(draftValue && isSameDay(date, draftValue));
                        const isCurrentMonthDay = isSameMonth(date, viewMonth);
                        const disabledDay = !isCurrentMonthDay || isDateDisabled(date, minDate, maxDate);
                        const isCurrentDay = isToday(date);

                        return (
                          <button
                            aria-label={formatDayLabel(date)}
                            aria-pressed={isSelected}
                            className={cn(
                              "inline-flex size-9 cursor-pointer items-center justify-center text-[14px] leading-[normal] tracking-[0px] transition-colors duration-150 ease-out",
                              isSelected
                                ? "rounded-[4px] border border-body-light-mode-1 bg-brand-primary-3 font-semibold text-body-light-mode-1"
                                : isCurrentDay
                                  ? "rounded-[4px] border border-gray-3 bg-transparent font-normal text-dark-5 hover:bg-brand-primary-3-20"
                                  : "rounded-[4px] font-normal text-dark-3 hover:bg-brand-primary-3-20",
                              disabledDay ? "cursor-not-allowed text-gray-4 opacity-50 hover:bg-transparent" : "cursor-pointer"
                            )}
                            data-id={buildDataId(dataId, "day", format(date, "yyyy-MM-dd"))}
                            data-slot="date-picker-day"
                            disabled={disabledDay}
                            key={date.getTime()}
                            onClick={() => handleDaySelect(date)}
                            type="button"
                          >
                            {format(date, "d")}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="flex max-h-[280px] flex-col px-6 pb-2" data-slot="date-picker-year-view" id={yearGridId}>
                    <div className="scrollbar-hidden grid max-h-[280px] grid-cols-4 gap-x-1 gap-y-3 overflow-y-auto pb-1 pr-1">
                      {yearRangeDisabled.map(item => {
                        const isSelectedYear = viewMonth.getFullYear() === item.year;

                        return (
                          <button
                            aria-pressed={isSelectedYear}
                            className={cn(
                              "inline-flex h-9 w-[72px] cursor-pointer items-center justify-center rounded-[4px] text-[14px] leading-[normal] tracking-[0px] transition-colors duration-150 ease-out",
                              isSelectedYear
                                ? "bg-brand-primary-3 font-semibold text-body-light-mode-1"
                                : "font-normal text-dark-3 hover:bg-brand-primary-3-20",
                              item.disabled ? "cursor-not-allowed opacity-50 hover:bg-transparent" : "cursor-pointer"
                            )}
                            data-id={buildDataId(dataId, "year", item.year)}
                            data-slot="date-picker-year"
                            disabled={item.disabled}
                            key={item.year}
                            onClick={() => handleYearSelect(item.year)}
                            type="button"
                          >
                            {item.year}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-end gap-2 px-4 py-2" data-slot="date-picker-footer">
                  <button
                    className="min-w-16 cursor-pointer rounded-[4px] px-2 py-1.5 text-[16px] font-semibold leading-[normal] tracking-[0px] text-brand-primary-3 transition-colors duration-150 hover:text-[oklch(0.3900_0.2301_292)]"
                    data-id={buildDataId(dataId, "clear-button")}
                    onClick={handleClear}
                    type="button"
                  >
                    Limpar
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </div>
  );
});

DsDatePicker.displayName = "DsDatePicker";

export { DsDatePicker, formatDatePickerValueForBackend };
export type { DsDatePickerProps, DsDatePickerValue };
